import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Badge,
  Input,
  Select,
  Spinner,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useColorModeValue,
} from "@chakra-ui/react";
import moment from "moment";
import { getBid } from "../../lib/Auth";
import { ivApi, isOk, WORKLIST_STATES, unverifiedPatients, checkBodyFromPrefill, checkOutcome, DEFAULT_CREDITS } from "./insuranceVerification";

/**
 * AppointmentEligibilityWorklist (SS-3944): upcoming appointments for a chosen range with
 * each patient's verification state (verified, stale, inactive, never), filters by day
 * and dentist, and "Check all unverified" — which shows the total cost before it runs.
 */

const CONCURRENCY = 4;

const runLimited = async (items, worker) => {
  let i = 0;
  const lanes = Array.from({ length: Math.min(CONCURRENCY, items.length) }, async () => {
    while (i < items.length) {
      const item = items[i++];
      await worker(item);
    }
  });
  await Promise.all(lanes);
};

const patientName = (p) => {
  const person = p?.dependent || p?.subscriber;
  return person ? [person.firstName, person.lastName].filter(Boolean).join(" ") : "";
};

export default function AppointmentEligibilityWorklist() {
  const bid = getBid();
  const [from, setFrom] = useState(moment().format("YYYY-MM-DD"));
  const [to, setTo] = useState(moment().add(7, "days").format("YYYY-MM-DD"));
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [settings, setSettings] = useState(null);
  const [prefills, setPrefills] = useState({});
  const [day, setDay] = useState("");
  const [dentist, setDentist] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState({});

  const cardBg = useColorModeValue("#FFFFFF", "#212330");
  const subColor = useColorModeValue("#64728C", "#A9B1C2");

  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    // only the latest range's answer lands: an older, slower one never replaces it
    const seq = ++loadSeq.current;
    setRows(null);
    setError("");
    const r = await ivApi.getWorklist(moment(from).startOf("day").toISOString(), moment(to).endOf("day").toISOString());
    if (seq !== loadSeq.current) return;
    if (!isOk(r)) {
      setError(r?.msg || "Could not load the appointments");
      setRows([]);
      return;
    }
    setRows(r.data || []);
    const ids = [...new Set((r.data || []).map((x) => x.patient_id).filter(Boolean))];
    await runLimited(ids, async (id) => {
      const p = await ivApi.getPrefill(id);
      if (seq === loadSeq.current && isOk(p)) setPrefills((cur) => ({ ...cur, [id]: p.data }));
    });
  }, [from, to]);

  useEffect(() => { load(); }, [load]);
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => { ivApi.getSettings().then((r) => { if (isOk(r)) setSettings(r.data); }); }, []);

  const dentists = useMemo(() => {
    const names = new Map((settings?.provider_npis || []).map((p) => [String(p.provider_id), [p.first_name, p.last_name].filter(Boolean).join(" ")]));
    return [...new Set((rows || []).map((r) => r.provider_id).filter(Boolean))].map((id) => ({ id, name: names.get(String(id)) || `Provider ${id}` }));
  }, [rows, settings]);
  const days = useMemo(() => [...new Set((rows || []).map((r) => moment(r.starttime).format("YYYY-MM-DD")))], [rows]);
  // a filter the loaded range does not offer (a day or dentist of the previous range) is cleared
  useEffect(() => {
    if (!rows) return;
    if (day && !days.includes(day)) setDay("");
    if (dentist && !dentists.some((d) => String(d.id) === dentist)) setDentist("");
  }, [rows, days, dentists, day, dentist]);
  const shown = (rows || []).filter((r) => (!day || moment(r.starttime).format("YYYY-MM-DD") === day) && (!dentist || String(r.provider_id) === dentist));
  const toCheck = unverifiedPatients(shown);
  const cost = settings?.credits?.eligibility ?? DEFAULT_CREDITS.eligibility;

  const checkAll = async () => {
    setConfirming(false);
    setRunning(true);
    try {
      await runLimited(toCheck, async (row) => {
        setResults((cur) => ({ ...cur, [row.patient_id]: { state: "running" } }));
        let p = prefills[row.patient_id];
        if (!p) {
          const r = await ivApi.getPrefill(row.patient_id);
          p = isOk(r) ? r.data : null;
        }
        if (!p) {
          setResults((cur) => ({ ...cur, [row.patient_id]: { state: "error", text: "Patient not found" } }));
          return;
        }
        // this appointment's day and dentist, not the patient's next visit
        const body = { ...checkBodyFromPrefill(p), serviceDate: moment(row.starttime).format("YYYY-MM-DD"), ...(row.provider_id ? { providerId: String(row.provider_id) } : {}) };
        const r = await ivApi.createVerification(row.patient_id, body);
        const text = isOk(r)
          ? checkOutcome(r.data)
          : r?.code === "missing_fields" ? "Missing details — open the patient" : (r?.msg || "Not sent");
        setResults((cur) => ({ ...cur, [row.patient_id]: { state: isOk(r) ? "done" : "error", text } }));
      });
    } finally {
      setRunning(false);
      loadRef.current();
    }
  };

  return (
    <Box p={{ base: 3, md: 6 }} w="100%" overflowY="auto" data-testid="iv-worklist">
      <Text fontSize="20px" fontWeight="700">Insurance worklist</Text>
      <Text fontSize="13px" color={subColor} mb="4">{"Upcoming appointments and each patient's insurance verification."}</Text>
      <Flex gap="3" wrap="wrap" align="end" mb="4">
        <Box><Text fontSize="12px" color={subColor}>From</Text><Input size="sm" type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="iv-worklist-from" /></Box>
        <Box><Text fontSize="12px" color={subColor}>To</Text><Input size="sm" type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="iv-worklist-to" /></Box>
        <Box>
          <Text fontSize="12px" color={subColor}>Day</Text>
          <Select size="sm" value={day} onChange={(e) => setDay(e.target.value)} placeholder="All days" data-testid="iv-worklist-day">
            {days.map((d) => <option key={d} value={d}>{moment(d).format("ddd, MMM D")}</option>)}
          </Select>
        </Box>
        <Box>
          <Text fontSize="12px" color={subColor}>Dentist</Text>
          <Select size="sm" value={dentist} onChange={(e) => setDentist(e.target.value)} placeholder="All dentists" data-testid="iv-worklist-dentist">
            {dentists.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </Select>
        </Box>
        <Button size="sm" bg="#154043" color="#F9F9FB" _hover={{ bg: "#123536" }} isDisabled={!toCheck.length || running}
          isLoading={running} onClick={() => setConfirming(true)} data-testid="iv-check-all">
          Check all unverified ({toCheck.length})
        </Button>
      </Flex>
      {error && <Text color="#B3261E" fontSize="13px" mb="2">{error}</Text>}
      {!rows && <Spinner />}
      {rows && shown.length === 0 && <Text fontSize="13px" color={subColor}>No appointments in this range.</Text>}
      <Box bg={cardBg} borderRadius="12px">
        {shown.map((r) => {
          const st = WORKLIST_STATES[r.state] || WORKLIST_STATES.never;
          const p = prefills[r.patient_id];
          const res = results[r.patient_id];
          return (
            <Flex key={`${r.patient_id || r.patient_guid}|${r.starttime}`} px="3" py="2" borderBottom="1px solid #E5E5EF" gap="3" align="center" wrap="wrap" data-testid="iv-worklist-row">
              <Text fontSize="13px" w="150px">{moment(r.starttime).format("ddd MMM D · h:mm A")}</Text>
              <Box flex="1" minW="160px">
                {r.patient_id ? (
                  <Link href={`/inbox/${r.patient_id}?bid=${bid}`} fontSize="13px" fontWeight="600">{patientName(p) || "Patient"}</Link>
                ) : <Text fontSize="13px" color={subColor}>Not linked in Viva</Text>}
                {p?.carrier_name && <Text fontSize="12px" color={subColor}>{p.carrier_name}</Text>}
              </Box>
              <Text fontSize="12px" color={subColor} w="120px">{dentists.find((d) => d.id === r.provider_id)?.name || ""}</Text>
              <Badge colorScheme={st.color} data-testid="iv-worklist-state">{st.label}</Badge>
              {res && <Text fontSize="12px" color={res.state === "error" ? "#B3261E" : subColor}>{res.state === "running" ? "Checking…" : res.text}</Text>}
            </Flex>
          );
        })}
      </Box>

      <Modal isOpen={confirming} onClose={() => setConfirming(false)} isCentered>
        <ModalOverlay />
        <ModalContent data-testid="iv-check-all-confirm">
          <ModalHeader fontSize="16px">Check all unverified</ModalHeader>
          <ModalBody fontSize="14px">
            <Text>{toCheck.length} patient{toCheck.length === 1 ? "" : "s"} × {cost} credits</Text>
            <Text fontWeight="700" mt="1" data-testid="iv-check-all-total">Total: {toCheck.length * cost} credits</Text>
            <Text fontSize="12px" color={subColor} mt="2">Patients with missing details are skipped and listed.</Text>
          </ModalBody>
          <ModalFooter gap="2">
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
            <Button size="sm" bg="#154043" color="#F9F9FB" _hover={{ bg: "#123536" }} onClick={checkAll} data-testid="iv-check-all-run">
              Run {toCheck.length} check{toCheck.length === 1 ? "" : "s"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
